/**
 * TrainingCatalogTab - Admin zakładka do zarządzania katalogiem szkoleń
 * (kategorie + typy) wojskaspecjalne.pl
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '../ui';
import {
  createTrainingCategory,
  createTrainingType,
  getAllTrainingCategories,
  getAllTrainingTypes,
  updateTrainingCategory,
  updateTrainingType,
  type TrainingCategory,
  type TrainingType,
} from '../../lib/trainingCatalogService';

interface TrainingCatalogTabProps {
  onToast?: (type: 'success' | 'error', text: string) => void;
}

export const TrainingCatalogTab: React.FC<TrainingCatalogTabProps> = ({ onToast }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [types, setTypes] = useState<TrainingType[]>([]);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TrainingCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    is_active: true,
  });

  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<TrainingType | null>(null);
  const [typeForm, setTypeForm] = useState({
    category_id: '' as string,
    name: '',
    slug: '',
    description: '',
    is_active: true,
  });

  const categoriesById = useMemo(() => {
    const map = new Map<string, TrainingCategory>();
    categories.forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const [catsRes, typesRes] = await Promise.all([
        getAllTrainingCategories({ includeInactive: true }),
        getAllTrainingTypes({ includeInactive: true }),
      ]);

      if (!catsRes.success) throw new Error(catsRes.error || 'Błąd ładowania kategorii');
      if (!typesRes.success) throw new Error(typesRes.error || 'Błąd ładowania typów');

      setCategories(catsRes.data || []);
      setTypes(typesRes.data || []);
    } catch (e: any) {
      console.error('[TrainingCatalogTab] loadAll error:', e);
      setError(e?.message || 'Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', slug: '', description: '', is_active: true });
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (cat: TrainingCategory) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      is_active: !!cat.is_active,
    });
    setCategoryDialogOpen(true);
  };

  const submitCategory = async () => {
    try {
      if (!categoryForm.name.trim()) {
        onToast?.('error', 'Nazwa kategorii jest wymagana');
        return;
      }

      const res = editingCategory
        ? await updateTrainingCategory({
            id: editingCategory.id,
            name: categoryForm.name,
            slug: categoryForm.slug || null,
            description: categoryForm.description || null,
            is_active: categoryForm.is_active,
          })
        : await createTrainingCategory({
            name: categoryForm.name,
            slug: categoryForm.slug || null,
            description: categoryForm.description || null,
            is_active: categoryForm.is_active,
          });

      if (!res.success) throw new Error(res.error || 'Błąd zapisu kategorii');

      onToast?.('success', editingCategory ? 'Kategoria zaktualizowana' : 'Kategoria dodana');
      setCategoryDialogOpen(false);
      await loadAll();
    } catch (e: any) {
      console.error('[TrainingCatalogTab] submitCategory error:', e);
      onToast?.('error', e?.message || 'Błąd zapisu kategorii');
    }
  };

  const toggleCategoryActive = async (cat: TrainingCategory) => {
    try {
      const res = await updateTrainingCategory({ id: cat.id, is_active: !cat.is_active });
      if (!res.success) throw new Error(res.error || 'Błąd zmiany statusu');
      await loadAll();
    } catch (e: any) {
      onToast?.('error', e?.message || 'Błąd zmiany statusu kategorii');
    }
  };

  const openAddType = () => {
    setEditingType(null);
    setTypeForm({ category_id: '', name: '', slug: '', description: '', is_active: true });
    setTypeDialogOpen(true);
  };

  const openEditType = (t: TrainingType) => {
    setEditingType(t);
    setTypeForm({
      category_id: t.category_id || '',
      name: t.name || '',
      slug: t.slug || '',
      description: t.description || '',
      is_active: !!t.is_active,
    });
    setTypeDialogOpen(true);
  };

  const submitType = async () => {
    try {
      if (!typeForm.name.trim()) {
        onToast?.('error', 'Nazwa typu jest wymagana');
        return;
      }

      const payload = {
        category_id: typeForm.category_id || null,
        name: typeForm.name,
        slug: typeForm.slug || null,
        description: typeForm.description || null,
        is_active: typeForm.is_active,
      };

      const res = editingType
        ? await updateTrainingType({ id: editingType.id, ...payload })
        : await createTrainingType(payload);

      if (!res.success) throw new Error(res.error || 'Błąd zapisu typu');

      onToast?.('success', editingType ? 'Typ zaktualizowany' : 'Typ dodany');
      setTypeDialogOpen(false);
      await loadAll();
    } catch (e: any) {
      console.error('[TrainingCatalogTab] submitType error:', e);
      onToast?.('error', e?.message || 'Błąd zapisu typu');
    }
  };

  const toggleTypeActive = async (t: TrainingType) => {
    try {
      const res = await updateTrainingType({ id: t.id, is_active: !t.is_active });
      if (!res.success) throw new Error(res.error || 'Błąd zmiany statusu');
      await loadAll();
    } catch (e: any) {
      onToast?.('error', e?.message || 'Błąd zmiany statusu typu');
    }
  };

  if (loading) {
    return <div className="admin-loading">Ładowanie katalogu szkoleń…</div>;
  }

  return (
    <div className="admin-content">
      <div className="admin-content-header">
        <h2 className="admin-content-title">Szkolenia</h2>
        <p className="content-subtitle">Katalog: kategorie i typy szkoleń (słowniki)</p>
      </div>

      {error && (
        <Alert variant="error" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Kategorie</h2>
          <Button variant="solid" onClick={openAddCategory}>
            + Dodaj kategorię
          </Button>
        </div>

        {categories.length === 0 ? (
          <Alert>
            <AlertDescription>Brak kategorii. Dodaj pierwszą kategorię.</AlertDescription>
          </Alert>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.name}</strong>
                      {c.description ? <div className="book-description">{c.description}</div> : null}
                    </td>
                    <td>{c.slug || '—'}</td>
                    <td>
                      <Badge variant="secondary">{c.is_active ? 'Aktywna' : 'Nieaktywna'}</Badge>
                    </td>
                    <td>
                      <div className="admin-table-actions">
                        <Button variant="outline" size="sm" onClick={() => openEditCategory(c)}>
                          Edytuj
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleCategoryActive(c)}
                          className={c.is_active ? 'border-orange-500 text-orange-500 hover:bg-orange-500/10' : ''}
                        >
                          {c.is_active ? 'Dezaktywuj' : 'Aktywuj'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Typy</h2>
          <Button variant="solid" onClick={openAddType}>
            + Dodaj typ
          </Button>
        </div>

        {types.length === 0 ? (
          <Alert>
            <AlertDescription>Brak typów. Dodaj pierwszy typ.</AlertDescription>
          </Alert>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Kategoria</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <strong>{t.name}</strong>
                      {t.description ? <div className="book-description">{t.description}</div> : null}
                    </td>
                    <td>{(t.category_id && categoriesById.get(t.category_id)?.name) || '—'}</td>
                    <td>{t.slug || '—'}</td>
                    <td>
                      <Badge variant="secondary">{t.is_active ? 'Aktywny' : 'Nieaktywny'}</Badge>
                    </td>
                    <td>
                      <div className="admin-table-actions">
                        <Button variant="outline" size="sm" onClick={() => openEditType(t)}>
                          Edytuj
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleTypeActive(t)}
                          className={t.is_active ? 'border-orange-500 text-orange-500 hover:bg-orange-500/10' : ''}
                        >
                          {t.is_active ? 'Dezaktywuj' : 'Aktywuj'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent size="4">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edytuj kategorię' : 'Dodaj kategorię'}</DialogTitle>
            <DialogDescription>Kategoria grupuje typy szkoleń.</DialogDescription>
          </DialogHeader>

          <Card variant="soft">
            <CardContent className="space-y-3">
              <div>
                <Label>Nazwa *</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="np. Medyczne / Taktyczne"
                />
              </div>

              <div>
                <Label>Slug</Label>
                <Input
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm((s) => ({ ...s, slug: e.target.value }))}
                  placeholder="np. medyczne"
                />
              </div>

              <div>
                <Label>Opis</Label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Krótki opis kategorii"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Aktywna</Label>
                <Switch
                  checked={categoryForm.is_active}
                  onCheckedChange={(checked) => setCategoryForm((s) => ({ ...s, is_active: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Anuluj
            </Button>
            <Button variant="solid" onClick={submitCategory}>
              Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent size="4">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edytuj typ' : 'Dodaj typ'}</DialogTitle>
            <DialogDescription>Typ będzie wybierany przy tworzeniu grup szkoleniowych.</DialogDescription>
          </DialogHeader>

          <Card variant="soft">
            <CardContent className="space-y-3">
              <div>
                <Label>Kategoria</Label>
                <Select
                  value={typeForm.category_id || 'none'}
                  onValueChange={(value) =>
                    setTypeForm((s) => ({ ...s, category_id: value === 'none' ? '' : value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz kategorię (opcjonalnie)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Nazwa *</Label>
                <Input
                  value={typeForm.name}
                  onChange={(e) => setTypeForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="np. TCCC / Strzelanie dynamiczne"
                />
              </div>

              <div>
                <Label>Slug</Label>
                <Input
                  value={typeForm.slug}
                  onChange={(e) => setTypeForm((s) => ({ ...s, slug: e.target.value }))}
                  placeholder="np. tccc"
                />
              </div>

              <div>
                <Label>Opis</Label>
                <Textarea
                  value={typeForm.description}
                  onChange={(e) => setTypeForm((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Krótki opis typu"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Aktywny</Label>
                <Switch
                  checked={typeForm.is_active}
                  onCheckedChange={(checked) => setTypeForm((s) => ({ ...s, is_active: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>
              Anuluj
            </Button>
            <Button variant="solid" onClick={submitType}>
              Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


